/**
 * NDJSON block grammar emitted by the AI worker over the SSE stream and
 * also persisted into `case_reports.content` / `session_notes.content` for
 * AI-generated drafts (until a Plan 3 conversion replaces them with
 * Tiptap content on first save). Shape mirrors
 * docs/client-frontend/14_session-summary-streaming.md §5.
 */

interface MetaBlock {
  type: 'meta';
  sessionId: string;
  clientName?: string;
  sessionType?: string;
  duration?: string;
  mode?: string;
  date?: string;
  participants?: { speaker_label: string; role: string; confidence: number }[];
}

interface SectionStartBlock {
  type: 'section_start';
  id: string;
  title: string;
}

interface MarkdownBlock {
  type: 'markdown';
  id: string;
  text: string;
}

interface TableBlock {
  type: 'table';
  id: string;
  title?: string;
  rows: string[][];
}

interface CalloutBlock {
  type: 'callout';
  /** Canonical levels are 'info' | 'warning' | 'danger', but BE may emit
   *  others — keep this open as `string` for forward-compat. */
  level: string;
  text: string;
}

interface EvidenceItem {
  claim: string;
  speaker_label: string;
  start: number;
  end: number;
  quote: string;
}

interface EvidenceBlock {
  type: 'evidence';
  id: string;
  items: EvidenceItem[];
}

interface SectionEndBlock {
  type: 'section_end';
  id: string;
}

interface FinalJsonBlock {
  type: 'final_json';
  clinical_note: Record<string, unknown>;
}

interface DoneBlock {
  type: 'done';
}

interface ErrorBlock {
  type: 'error';
  message: string;
}

export type NdjsonBlock =
  | MetaBlock
  | SectionStartBlock
  | MarkdownBlock
  | TableBlock
  | CalloutBlock
  | EvidenceBlock
  | SectionEndBlock
  | FinalJsonBlock
  | DoneBlock
  | ErrorBlock;

const KNOWN_TYPES = new Set<NdjsonBlock['type']>([
  'meta',
  'section_start',
  'markdown',
  'table',
  'callout',
  'evidence',
  'section_end',
  'final_json',
  'done',
  'error',
]);

/**
 * Parse one NDJSON line into a typed block. Returns null on malformed JSON
 * or unknown `type` (the latter is defensive — BE may add new block kinds
 * before the FE knows about them; we'd rather skip than crash).
 */
export function parseBlock(line: string): NdjsonBlock | null {
  if (!line) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(line);
  } catch {
    return null;
  }
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    !('type' in parsed) ||
    typeof parsed.type !== 'string'
  ) {
    return null;
  }
  const t = (parsed as { type: string }).type;
  if (!KNOWN_TYPES.has(t as NdjsonBlock['type'])) return null;
  return parsed as NdjsonBlock;
}
